/**
 * CR310: Ribbon Asset Picker for Deal Cards → Components → Ribbon
 * 
 * Allows admins to select a ribbon graphic from Asset Studio.
 * Uses the shared AssetPickerModal with filterType="ribbon".
 */

import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getAssetImageSrc } from "@/lib/assets/getAppAssetPublicUrl";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { 
  AlertTriangle, 
  ExternalLink, 
  Flag, 
  Sparkles, 
  X 
} from "lucide-react";
import { toast } from "sonner";
import { useAssetsByType } from "@/hooks/useAssetsByType";
import { useAppAssets } from "@/hooks/useAppAssets";
import { PLACEHOLDER_SVGS } from "@/lib/asset-studio/types";
import type { AppAsset } from "@/lib/asset-studio/types";
import { AssetPickerModal } from "./AssetPickerModal";

// ================================================================
// TYPES
// ================================================================

export type RibbonGraphicSource = "system" | "asset";

interface RibbonAssetPickerProps {
  /** Current graphic source */
  graphicSource: RibbonGraphicSource;
  /** Currently selected asset ID (when source is "asset") */
  selectedAssetId: string | null;
  /** Whether the ribbon feature is enabled */
  ribbonEnabled: boolean;
  /** Callback when user changes source/asset */
  onChange: (source: RibbonGraphicSource, assetId: string | null) => void;
}

// ================================================================
// COMPONENT
// ================================================================

export function RibbonAssetPicker({ 
  graphicSource,
  selectedAssetId, 
  ribbonEnabled,
  onChange,
}: RibbonAssetPickerProps) {
  const navigate = useNavigate();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [seeding, setSeeding] = useState(false);
  
  // Load ribbon assets
  const { 
    assets: ribbonAssets, 
    loading: ribbonAssetsLoading,
  } = useAssetsByType({ type: "ribbon" });
  
  const { createAsset } = useAppAssets();
  
  // Find selected asset
  const selectedAsset = useMemo(() => 
    ribbonAssets.find(a => a.id === selectedAssetId) ?? null,
    [ribbonAssets, selectedAssetId]
  );
  
  // Check if we have any ribbon assets
  const hasRibbonAssets = ribbonAssets.length > 0;
  
  // Check for missing asset warning
  const isMissingAsset = graphicSource === "asset" && selectedAssetId && !selectedAsset;
  
  // Handlers
  const handleSourceChange = (source: RibbonGraphicSource) => {
    if (source === "system") {
      onChange("system", null);
    } else {
      onChange("asset", selectedAssetId);
    }
  };
  
  const handleSelectFromModal = (asset: { id: string; name: string } | null) => {
    if (asset) {
      onChange("asset", asset.id);
      toast.success("Ribbon asset selected");
    } else {
      onChange("system", null);
      toast.info("Cleared to system default");
    }
  };
  
  const handleClear = () => {
    onChange("system", null);
    toast.info("Returned to system default ribbon");
  };
  
  const handleOpenAssetStudio = () => {
    navigate("/admin/asset-studio");
  };
  
  const handleSeedDefault = async () => {
    setSeeding(true);
    try {
      // CR337: Seed legacy corner ribbon (matches production clip-path)
      const legacyCorner = PLACEHOLDER_SVGS["ribbon_legacy_corner"];
      if (legacyCorner) {
        // Skip if already exists
        const alreadyExists = ribbonAssets.some(a => a.name === legacyCorner.name);
        if (alreadyExists) {
          toast.info("Legacy Corner Ribbon already exists");
        } else {
          const result = await createAsset({
            name: legacyCorner.name,
            type: legacyCorner.type,
            svg: legacyCorner.svg,
            tags: legacyCorner.tags,
            is_tileable: false,
          });
          if (result) {
            toast.success("Legacy Corner Ribbon created");
          }
        }
      }
    } catch (err) {
      console.error("[RibbonAssetPicker] Seed error:", err);
      toast.error("Failed to seed ribbon asset");
    } finally {
      setSeeding(false);
    }
  };
  
  // ================================================================
  // RENDER: Loading state
  // ================================================================
  
  if (ribbonAssetsLoading) {
    return (
      <div className="space-y-3">
        <Label className="text-xs text-muted-foreground mb-2 block flex items-center gap-1.5">
          <Flag className="h-3 w-3" />
          Ribbon Graphic
        </Label>
        <Skeleton className="h-20 w-full rounded-lg" />
      </div>
    );
  }
  
  // ================================================================
  // RENDER: Disabled state (ribbon toggle is OFF)
  // ================================================================
  
  if (!ribbonEnabled) {
    return (
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground mb-2 block flex items-center gap-1.5">
          <Flag className="h-3 w-3" />
          Ribbon Graphic
        </Label>
        <div className="p-3 rounded-lg bg-muted/30 border border-dashed">
          <p className="text-xs text-muted-foreground text-center">
            Enable Ribbon to select a graphic.
          </p>
        </div>
      </div>
    );
  }
  
  // ================================================================
  // RENDER: Main UI
  // ================================================================
  
  return (
    <div className="space-y-3">
      <Label className="text-xs text-muted-foreground mb-2 block flex items-center gap-1.5">
        <Flag className="h-3 w-3" />
        Ribbon Graphic
      </Label>
      
      {/* Source Selection */}
      <RadioGroup 
        value={graphicSource} 
        onValueChange={(v) => handleSourceChange(v as RibbonGraphicSource)}
        className="flex gap-4"
      >
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="system" id="ribbon-system" />
          <Label htmlFor="ribbon-system" className="text-sm cursor-pointer">
            System Default
          </Label>
        </div>
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="asset" id="ribbon-asset" />
          <Label htmlFor="ribbon-asset" className="text-sm cursor-pointer">
            Asset Studio
          </Label>
        </div>
      </RadioGroup>
      
      {/* Asset selection when "Asset Studio" is selected */}
      {graphicSource === "asset" && (
        <div className="space-y-3 pl-6 border-l-2 border-muted">
          {/* Missing asset warning */}
          {isMissingAsset && (
            <Alert variant="destructive" className="py-2">
              <AlertTriangle className="h-3 w-3" />
              <AlertDescription className="text-xs">
                Selected ribbon asset not found — falling back to System Default.
              </AlertDescription>
            </Alert>
          )}
          
          {/* No assets warning */}
          {!hasRibbonAssets && (
            <Alert className="py-2 border-dashed">
              <AlertTriangle className="h-3 w-3 text-muted-foreground" />
              <AlertTitle className="text-xs">No Ribbon Assets</AlertTitle>
              <AlertDescription className="text-xs mt-1">
                Create one in Asset Studio → New Asset → Type: Ribbon.
              </AlertDescription>
            </Alert>
          )}
          
          {/* Selected asset display */}
          {selectedAsset && (
            <div className="flex items-center gap-3 p-3 rounded-lg border bg-card">
              <img 
                src={getAssetImageSrc(selectedAsset) || ""}
                alt={selectedAsset.name}
                className="w-10 h-10 flex-shrink-0 object-contain"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium truncate">
                    {selectedAsset.name}
                  </span>
                  <Badge variant="outline" className="text-[9px] px-1 py-0 flex-shrink-0">
                    Ribbon
                  </Badge>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleClear}
                className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
          
          {/* Actions */}
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setPickerOpen(true)}
              disabled={!hasRibbonAssets}
              className="text-xs gap-1.5"
            >
              {selectedAsset ? "Change" : "Select Ribbon..."}
            </Button>
            
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleOpenAssetStudio}
              className="text-xs gap-1.5"
            >
              <ExternalLink className="h-3 w-3" />
              Asset Studio
            </Button>
            
            {!hasRibbonAssets && (
              <Button 
                variant="secondary" 
                size="sm" 
                onClick={handleSeedDefault}
                disabled={seeding}
                className="text-xs gap-1.5"
              >
                <Sparkles className="h-3 w-3" />
                {seeding ? "Creating..." : "Seed Default"}
              </Button>
            )}
          </div>
        </div>
      )}
      
      {/* Asset Picker Modal */}
      <AssetPickerModal
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        filterType="ribbon"
        assets={ribbonAssets}
        selectedId={selectedAssetId}
        onSelect={handleSelectFromModal}
        title="Select Ribbon Asset"
        allowClear={true}
      />
    </div>
  );
}
